/**
 * Per-session ridge from CNN features to screen position — the second stage of
 * Miłosz's engine, as the v0.1.0 container runs it (baseline/calibrator.py +
 * runtime/engine.py).
 *
 * The container fits one fixed spec, not a searched one. FaceWise sends the
 * study variant (`quick-4x1`, …) as the preset and the image pins a spec only
 * for `quick`, so every real session gets DEFAULT_SPEC. The lab uses the same,
 * so a lab number and a study number mean the same thing.
 *
 * Fit: take the spec's columns → z-score (population std, zero std → 1) →
 * design matrix [1, x] → ridge with the intercept unpenalised, separate λ for
 * x and y. The targets' unit does not matter: the solution is linear in y.
 */
import { ridgeFit } from './ridge'

export interface CalibratorSpec {
  features: number[]
  degree: 1
  lamX: number
  lamY: number
}

/** runtime/engine.py DEFAULT_SPEC @ v0.1.0: pitch + yaw only, linear, λx 1, λy 100. */
export const CONTAINER_SPEC: CalibratorSpec = { features: [0, 1], degree: 1, lamX: 1, lamY: 100 }

const MIN_SAMPLES = 10

export class CnnCalibrator {
  private X: number[][] = []
  private Y: Array<[number, number]> = []
  private mean: number[] | null = null
  private std: number[] | null = null
  private wx: number[] | null = null
  private wy: number[] | null = null

  readonly spec: CalibratorSpec

  constructor(spec: CalibratorSpec = CONTAINER_SPEC) {
    this.spec = spec
  }

  add(features: number[], x: number, y: number): void {
    this.X.push(features)
    this.Y.push([x, y])
  }

  get sampleCount(): number {
    return this.X.length
  }

  get isFitted(): boolean {
    return this.wx !== null
  }

  /** Raw rows, for the session export. */
  samples(): { features: number[]; x: number; y: number }[] {
    return this.X.map((f, i) => ({ features: f, x: this.Y[i][0], y: this.Y[i][1] }))
  }

  clear(): void {
    this.X = []
    this.Y = []
    this.mean = this.std = this.wx = this.wy = null
  }

  private select(f: number[]): number[] {
    return this.spec.features.map((i) => f[i])
  }

  private design(f: number[]): number[] {
    const sel = this.select(f)
    return [1, ...sel.map((v, i) => (v - this.mean![i]) / this.std![i])]
  }

  fit(): boolean {
    if (this.X.length < MIN_SAMPLES) return false
    const rows = this.X.map((f) => this.select(f))
    const k = rows[0].length
    const mean = new Array<number>(k).fill(0)
    const std = new Array<number>(k).fill(0)
    for (const r of rows) for (let i = 0; i < k; i++) mean[i] += r[i] / rows.length
    for (const r of rows) for (let i = 0; i < k; i++) std[i] += (r[i] - mean[i]) ** 2 / rows.length
    for (let i = 0; i < k; i++) std[i] = Math.sqrt(std[i]) || 1
    this.mean = mean
    this.std = std

    const D = this.X.map((f) => this.design(f))
    const wx = ridgeFit(D, this.Y.map((t) => t[0]), this.spec.lamX)
    const wy = ridgeFit(D, this.Y.map((t) => t[1]), this.spec.lamY)
    if (!wx || !wy) return false
    this.wx = wx
    this.wy = wy
    return true
  }

  predict(features: number[]): { x: number; y: number } | null {
    if (!this.wx || !this.wy) return null
    const d = this.design(features)
    let x = 0
    let y = 0
    for (let i = 0; i < d.length; i++) {
      x += this.wx[i] * d[i]
      y += this.wy[i] * d[i]
    }
    return { x, y }
  }
}
